/**
 * Shared presentational markup for every public SEO page.
 *
 * These are plain-JS React components built with `React.createElement` (no JSX,
 * no TypeScript) for one specific reason: the exact same component tree is
 * rendered twice —
 *
 *   • at BUILD time by `scripts/generate-seo-assets.js` via
 *     `react-dom/server`'s `renderToStaticMarkup`, producing the real HTML a
 *     crawler receives without executing any JavaScript; and
 *   • at RUN time by the React pages in `src/pages/public/`, so a human sees
 *     the identical content, then gets the interactive SPA on top.
 *
 * Keeping one component tree is what guarantees the crawler and the user are
 * looking at the same page. There is no hidden text, no crawler-only block and
 * no cloaking: the prerendered markup IS the visible page.
 *
 * Accessibility/semantics are part of the contract here: exactly one <h1> per
 * page, a logical h1 -> h2 -> h3 hierarchy, descriptive link text (never "click
 * here"), and real alt text on every image.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

const React = require('react');
const { resolveServiceCategory } = require('./taxonomy');
const { formatPlace, formatPriceRange, formatRating } = require('./metadata');

const h = React.createElement;

/**
 * Lets the SPA swap plain <a> for react-router <Link> so in-app navigation
 * stays client-side, while the prerenderer emits crawlable <a href> elements.
 * @type {React.Context<null | ((props: Object) => React.ReactElement)>}
 */
const LinkRendererContext = React.createContext(null);

/**
 * @param {{ to: string, children: React.ReactNode, className?: string, rel?: string }} props
 */
function SeoLink(props) {
  const renderLink = React.useContext(LinkRendererContext);
  if (renderLink) return renderLink(props);
  return h(
    'a',
    { href: props.to, className: props.className, rel: props.rel },
    props.children
  );
}

/**
 * @param {{ trail: Array<{ name: string, path: string }> }} props
 */
function Breadcrumbs(props) {
  const trail = Array.isArray(props.trail) ? props.trail : [];
  if (trail.length < 2) return null;

  return h(
    'nav',
    { className: 'seo-breadcrumbs', 'aria-label': 'Breadcrumb' },
    h(
      'ol',
      { className: 'seo-breadcrumbs__list' },
      trail.map((crumb, index) => {
        const isLast = index === trail.length - 1;
        return h(
          'li',
          { key: crumb.path, className: 'seo-breadcrumbs__item' },
          isLast
            ? h('span', { 'aria-current': 'page' }, crumb.name)
            : h(SeoLink, { to: crumb.path }, crumb.name)
        );
      })
    )
  );
}

/**
 * One stylist result. The whole card is a single descriptive link so the
 * anchor text is the professional's name and role, not "view profile".
 *
 * @param {{ stylist: Object }} props
 */
function StylistResultCard(props) {
  const stylist = props.stylist || {};
  const category = resolveServiceCategory(stylist.primaryCategory);
  const role = category ? category.providerNoun.replace(/s$/, '') : 'Beauty professional';
  const place = formatPlace(stylist.city, stylist.state);
  const rating = formatRating(stylist.ratingAverage, stylist.ratingCount);
  const priceRange = formatPriceRange(stylist.priceMin, stylist.priceMax);
  const label = place ? `${stylist.displayName}, ${role} in ${place}` : stylist.displayName;

  return h(
    'li',
    { className: 'seo-stylist-card' },
    h(
      SeoLink,
      { to: `/stylists/${stylist.seoSlug}`, className: 'seo-stylist-card__link' },
      stylist.profilePhotoUrl
        ? h('img', {
            className: 'seo-stylist-card__photo',
            src: stylist.profilePhotoUrl,
            // Real, descriptive alt text — never keyword stuffing.
            alt: `Profile photo of ${label}`,
            width: 96,
            height: 96,
            // Below-the-fold results are lazy; sizes are explicit to avoid CLS.
            loading: 'lazy',
            decoding: 'async',
          })
        : h(
            'span',
            { className: 'seo-stylist-card__photo seo-stylist-card__photo--empty', 'aria-hidden': 'true' },
            String(stylist.displayName || '?').trim().charAt(0).toUpperCase()
          ),
      h(
        'span',
        { className: 'seo-stylist-card__body' },
        h('span', { className: 'seo-stylist-card__name' }, stylist.displayName),
        h('span', { className: 'seo-stylist-card__meta' }, place ? `${role} · ${place}` : role),
        rating ? h('span', { className: 'seo-stylist-card__rating' }, `Rated ${rating}`) : null,
        priceRange ? h('span', { className: 'seo-stylist-card__price' }, `From ${priceRange}`) : null,
        stylist.shortBio
          ? h('span', { className: 'seo-stylist-card__bio' }, stylist.shortBio)
          : null
      )
    )
  );
}

/**
 * @param {{ heading: string, stylists: Array<Object>, emptyState?: Object }} props
 */
function StylistResultList(props) {
  const stylists = Array.isArray(props.stylists) ? props.stylists : [];

  if (stylists.length === 0) {
    const empty = props.emptyState || {};
    return h(
      'section',
      { className: 'seo-section seo-section--empty' },
      h('h2', null, empty.heading || 'No professionals listed here yet'),
      h(
        'p',
        null,
        empty.body ||
          'We have not onboarded a professional for this search yet. Browse nearby options, or join LookBel as a provider.'
      ),
      empty.ctaPath
        ? h(
            'p',
            null,
            h(SeoLink, { to: empty.ctaPath, className: 'seo-cta' }, empty.ctaLabel || 'Join LookBel as a professional')
          )
        : null
    );
  }

  return h(
    'section',
    { className: 'seo-section' },
    h('h2', null, props.heading),
    h(
      'ul',
      { className: 'seo-stylist-list' },
      stylists.map((stylist) =>
        h(StylistResultCard, { key: stylist.seoSlug || stylist.displayName, stylist })
      )
    )
  );
}

/**
 * A block of internal links (national -> state -> city -> service -> stylist
 * and back). Rendered only for real inventory; we never emit a sitewide
 * directory of every possible city x service permutation.
 *
 * @param {{ group: { heading: string, description?: string, items: Array<{ label: string, path: string, meta?: string }> } }} props
 */
function InternalLinkGroup(props) {
  const group = props.group || {};
  const items = Array.isArray(group.items) ? group.items : [];
  if (items.length === 0) return null;

  return h(
    'section',
    { className: 'seo-section seo-links' },
    h('h2', null, group.heading),
    group.description ? h('p', null, group.description) : null,
    h(
      'ul',
      { className: 'seo-links__list' },
      items.map((item) =>
        h(
          'li',
          { key: item.path },
          h(SeoLink, { to: item.path }, item.label),
          item.meta ? h('span', { className: 'seo-links__meta' }, ` — ${item.meta}`) : null
        )
      )
    )
  );
}

/**
 * Public stylist profile detail. Deliberately renders ONLY public fields.
 * Email, phone number, exact street address and anything payment-related are
 * not part of the public profile model and therefore cannot be rendered here.
 *
 * @param {{ profile: Object }} props
 */
function StylistProfileDetail(props) {
  const profile = props.profile || {};
  const place = formatPlace(profile.city, profile.state);
  const rating = formatRating(profile.ratingAverage, profile.ratingCount);
  const priceRange = formatPriceRange(profile.priceMin, profile.priceMax);
  const services = Array.isArray(profile.services) ? profile.services : [];
  const category = resolveServiceCategory(profile.primaryCategory);

  return h(
    'div',
    { className: 'seo-profile' },
    h(
      'section',
      { className: 'seo-section seo-profile__header' },
      profile.profilePhotoUrl
        ? h('img', {
            className: 'seo-profile__photo',
            src: profile.profilePhotoUrl,
            alt: `Profile photo of ${profile.displayName}${place ? ` in ${place}` : ''}`,
            width: 160,
            height: 160,
            // Above the fold on this page — load eagerly so LCP is the photo.
            loading: 'eager',
            decoding: 'async',
          })
        : null,
      h(
        'p',
        { className: 'seo-profile__summary' },
        [
          category ? category.providerNoun.replace(/s$/, '') : null,
          place ? `serving ${place}` : null,
        ]
          .filter(Boolean)
          .join(' ')
      ),
      rating ? h('p', { className: 'seo-profile__rating' }, `Rated ${rating}`) : null,
      priceRange ? h('p', { className: 'seo-profile__price' }, `Services from ${priceRange}`) : null
    ),
    profile.fullBio || profile.shortBio
      ? h(
          'section',
          { className: 'seo-section' },
          h('h2', null, `About ${profile.displayName}`),
          h('p', null, profile.fullBio || profile.shortBio)
        )
      : null,
    services.length
      ? h(
          'section',
          { className: 'seo-section' },
          h('h2', null, 'Services offered'),
          h(
            'ul',
            { className: 'seo-profile__services' },
            services.map((service) => {
              const servicePrice = formatPriceRange(service.priceMin, service.priceMax);
              return h(
                'li',
                { key: service.name },
                h('span', { className: 'seo-profile__service-name' }, service.name),
                servicePrice
                  ? h('span', { className: 'seo-profile__service-price' }, ` — ${servicePrice}`)
                  : null
              );
            })
          )
        )
      : null
  );
}

/**
 * The full public page. One <h1>, then <h2> sections, then internal links.
 *
 * @param {{ model: Object }} props
 */
function PublicPageContent(props) {
  const model = props.model || {};
  const metadata = model.metadata || {};
  const linkGroups = Array.isArray(model.linkGroups) ? model.linkGroups : [];

  return h(
    'main',
    { className: `seo-page seo-page--${model.kind || 'generic'}`, id: 'main-content' },
    h(Breadcrumbs, { trail: model.breadcrumbs }),
    h('h1', { className: 'seo-page__title' }, metadata.h1 || metadata.title),
    model.intro ? h('p', { className: 'seo-page__intro' }, model.intro) : null,
    model.profile ? h(StylistProfileDetail, { profile: model.profile }) : null,
    model.showStylistList === false
      ? null
      : h(StylistResultList, {
          heading: model.stylistListHeading || 'Professionals',
          stylists: model.stylists,
          emptyState: model.emptyState,
        }),
    linkGroups.map((group, index) => h(InternalLinkGroup, { key: group.heading || index, group }))
  );
}

module.exports = {
  LinkRendererContext,
  SeoLink,
  Breadcrumbs,
  StylistResultCard,
  StylistResultList,
  InternalLinkGroup,
  StylistProfileDetail,
  PublicPageContent,
};
